import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Leaf, Search, RefreshCw, Upload, Loader2, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const buildName = (it) =>
  [it.genero, it.especie, it.cultivar, it.variegacion, it["tamaño"]]
    .map((x) => String(x || "").trim())
    .filter((x) => x && normalize(x) !== "no")
    .join(" ")
    .trim() || it.product_id || "Producto";

const isAvailable = (status) => normalize(status) === "disponible";
const isUnavailable = (status) => {
  const s = normalize(status);
  return s === "no disponible" || s === "perdida";
};

const asInput = (value) => (value === null || value === undefined ? "" : String(value));

function StockBadge({ item }) {
  if (!isAvailable(item.status)) {
    return (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-destructive/15 text-destructive">
        {item.status || "No disponible"}
      </span>
    );
  }
  if (Number(item.stock_qty || 0) <= 1) {
    return (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400">
        Stock {Number(item.stock_qty || 0)}
      </span>
    );
  }
  return null;
}

export default function InventoryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [availability, setAvailability] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [removingImage, setRemovingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const sheetRes = await api.get("/gsheet/catalog");
      const rows = (Array.isArray(sheetRes.data) ? sheetRes.data : []).map((sheetItem) => ({
        ...sheetItem,
        name: buildName(sheetItem),
        image: String(sheetItem.image_url || "").trim(),
      }));
      setItems(rows);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar el catálogo real.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = normalize(search);
    return items.filter((it) => {
      if (availability === "available" && !isAvailable(it.status)) return false;
      if (availability === "unavailable" && !isUnavailable(it.status)) return false;
      const hay = normalize(`${it.product_id} ${it.name} ${it.product_type} ${it.ubicacion_id}`);
      return hay.includes(q);
    });
  }, [items, search, availability]);

  const openEditor = (item) => {
    setSelected(item);
    setForm({
      genero: asInput(item.genero),
      especie: asInput(item.especie),
      cultivar: asInput(item.cultivar),
      variegacion: asInput(item.variegacion),
      "tamaño": asInput(item["tamaño"]),
      product_type: asInput(item.product_type),
      status: asInput(item.status),
      stock_qty: asInput(item.stock_qty),
      costo_unitario: asInput(item.costo_unitario),
      precio_sugerido: asInput(item.precio_sugerido),
      ubicacion_id: asInput(item.ubicacion_id),
    });
    setImage(String(item.image_url || item.image || "").trim());
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected?.product_id) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("product_id", String(selected.product_id));
      fd.append("file", file);

      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setImage(String(data?.image_url || ""));
      setSelected((prev) => prev ? {
        ...prev,
        image_url: data?.image_url || "",
        image_file_id: data?.image_file_id || "",
      } : prev);
      toast.success("Imagen guardada en Google Drive y vinculada al producto.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar la imagen.", { duration: 8000 });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = async () => {
    if (!selected?.product_id || !image) return;
    setRemovingImage(true);
    try {
      await api.post("/gsheet/remove-catalog-image", {
        product_id: String(selected.product_id),
      });
      setImage("");
      setSelected((prev) => prev ? { ...prev, image_url: "", image_file_id: "" } : prev);
      toast.success("Imagen eliminada del producto.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar la imagen.", { duration: 8000 });
    } finally {
      setRemovingImage(false);
    }
  };

  const saveProduct = async () => {
    if (!selected?.product_id) return toast.error("El producto no tiene product_id.");
    setSaving(true);
    try {
      await api.post("/gsheet/update-catalog-product", {
        product_id: String(selected.product_id),
        fields: form,
      });
      toast.success("Producto actualizado en Inventario_Plantas.");
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el producto.", { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const previewName = selected ? buildName({ ...selected, ...form }) : "";

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="admin-inventory-table">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-foreground">Catálogo</h1>
          <p className="text-sm text-muted-foreground">
            {items.length} productos · Inventario_Plantas + imágenes en Google Drive
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-border p-1 bg-card">
            <Button type="button" size="sm" variant={availability === "all" ? "default" : "ghost"} onClick={() => setAvailability("all")}>Todos</Button>
            <Button type="button" size="sm" variant={availability === "available" ? "default" : "ghost"} onClick={() => setAvailability("available")}>Disponibles</Button>
            <Button type="button" size="sm" variant={availability === "unavailable" ? "default" : "ghost"} onClick={() => setAvailability("unavailable")}>No disponibles</Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-44" />
          </div>
          <Button variant="outline" onClick={load} disabled={loading} className="gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Actualizar
          </Button>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((it) => (
            <div key={it.product_id} className="bg-card rounded-2xl border border-border p-4 flex gap-4 items-center shadow-sm">
              <div className="h-20 w-20 rounded-xl overflow-hidden bg-secondary shrink-0 flex items-center justify-center">
                {it.image ? <img src={it.image} alt={it.name} className="h-full w-full object-cover" /> : <Leaf className="h-7 w-7 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[10px] font-semibold text-accent uppercase">{it.product_type || "Producto"}</p>
                  <StockBadge item={it} />
                </div>
                <p className="font-semibold text-foreground line-clamp-2">{it.name}</p>
                <p className="font-mono text-sm text-foreground">{money(Number(it.precio_sugerido || 0))}</p>
                <p className="text-xs text-muted-foreground">Stock {Number(it.stock_qty || 0)} · {it.product_id}</p>
              </div>
              {isAdmin && (
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditor(it)} data-testid={`admin-edit-product-${it.product_id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {filtered.length === 0 && <p className="col-span-full text-center text-muted-foreground py-12">No hay productos para este filtro.</p>}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Editar producto</DialogTitle>
            <DialogDescription>
              Los datos y la referencia de imagen se guardan en Inventario_Plantas; el archivo de imagen vive en Google Drive.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-6">
              <div className="rounded-xl border border-border bg-secondary/30 p-3">
                <p className="text-xs text-muted-foreground font-mono">{selected.product_id}</p>
                <p className="font-semibold mt-1">{previewName}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Última venta registrada: {selected.precio_venta ? money(Number(selected.precio_venta)) : "—"}
                </p>
              </div>

              <section className="space-y-3">
                <div>
                  <h3 className="font-semibold">Datos del producto</h3>
                  <p className="text-xs text-muted-foreground">Editables en la app y sincronizados hacia Google Sheets.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Género" value={form.genero} onChange={(v) => setField("genero", v)} />
                  <Field label="Especie" value={form.especie} onChange={(v) => setField("especie", v)} />
                  <Field label="Cultivar" value={form.cultivar} onChange={(v) => setField("cultivar", v)} />
                  <Field label="Variegación" value={form.variegacion} onChange={(v) => setField("variegacion", v)} />
                  <Field label="Tamaño" value={form["tamaño"]} onChange={(v) => setField("tamaño", v)} />
                  <Field label="Tipo de producto" value={form.product_type} onChange={(v) => setField("product_type", v)} />
                  <div className="space-y-1.5">
                    <Label>Estado</Label>
                    <select
                      value={form.status || ""}
                      onChange={(e) => setField("status", e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Seleccionar…</option>
                      <option value="Disponible">Disponible</option>
                      <option value="No Disponible">No Disponible</option>
                      <option value="Perdida">Perdida</option>
                    </select>
                  </div>
                  <Field label="Ubicación" value={form.ubicacion_id} onChange={(v) => setField("ubicacion_id", v)} />
                  <Field label="Stock" type="number" min="0" step="1" value={form.stock_qty} onChange={(v) => setField("stock_qty", v)} />
                  <Field label="Costo unitario" type="number" min="0" step="0.01" value={form.costo_unitario} onChange={(v) => setField("costo_unitario", v)} />
                  <Field label="Precio sugerido" type="number" min="0" step="0.01" value={form.precio_sugerido} onChange={(v) => setField("precio_sugerido", v)} />
                </div>
              </section>

              <section className="space-y-3 border-t border-border pt-5">
                <div>
                  <h3 className="font-semibold">Imagen</h3>
                  <p className="text-xs text-muted-foreground">JPG, PNG o WEBP. La carga se guarda inmediatamente en la carpeta Catalog_Images de Google Drive.</p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="h-28 w-28 rounded-xl overflow-hidden bg-secondary flex items-center justify-center shrink-0">
                    {image ? <img src={image} alt="preview" className="h-full w-full object-cover" /> : <Leaf className="h-7 w-7 text-muted-foreground" />}
                  </div>
                  <div className="space-y-2 flex-1">
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" />
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" disabled={uploading || removingImage} onClick={() => fileRef.current?.click()} className="gap-1.5">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {image ? "Cambiar imagen" : "Subir imagen"}
                      </Button>
                      {image && (
                        <Button type="button" variant="outline" disabled={uploading || removingImage} onClick={removeImage} className="gap-1.5 text-destructive">
                          {removingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Eliminar imagen
                        </Button>
                      )}
                    </div>
                    {image && <p className="text-xs text-muted-foreground break-all">Imagen vinculada desde Google Drive.</p>}
                  </div>
                </div>
              </section>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Cancelar</Button>
            <Button onClick={saveProduct} disabled={saving || uploading || removingImage}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar producto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", min, step }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type={type}
        min={min}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
